import { useDispatch } from 'react-redux';
import {
	Col,
	Alert,
	Spinner,
	CardBody,
	Card,
	CardHeader,
	Row,
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getPowerGenerationMaintenanceList } from '../../../slices/powerGenerationMaintenance/thunk';
import AddEditPowerGenerationMaintenanceModal from './Add_Edit/AddEditPowerGenerationMaintenance';
import DeletePowerGenerationMaintenanceModal from './Delete/DeletePowerGenerationMaintenance';

const PowerGenerationMaintenanceList = () => {
	const dispatch = useDispatch();
	const history = useNavigate();

	const selectorPowerGenerationMaintenance = createSelector(
		state => state.PowerGenerationMaintenance,
		data => ({
			powerGenerationMaintenanceList: data.powerGenerationMaintenanceList,
			isLoadingList: data.isLoadingList,
			isPowerGenerationMaintenanceSuccess:
				data.isPowerGenerationMaintenanceSuccess,
		})
	);

	const { powerGenerationMaintenanceList, isLoadingList } = useSelector(
		selectorPowerGenerationMaintenance
	);

	useEffect(() => {
		dispatch(getPowerGenerationMaintenanceList());
	}, []);

	const [dataPowerGenerationMaintenance, setDataPowerGenerationMaintenance] =
		useState({});
	const [isEdit, setIsEdit] = useState(false);

	// Delete PowerGenerationMaintenance
	const [modal, setModal] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	//Tab
	const [activeTab, setActiveTab] = useState('1');
	const toggleTab = tab => {
		if (activeTab !== tab) {
			setActiveTab(tab);
		}
	};

	function handleToggleModal() {
		setModal(!modal);
		toggleTab('1');
		// validation.resetForm();
	}

	function handleToggleModalDelete() {
		setModalDelete(!modalDelete);
	}

	// Delete Data
	const handleClickDelete = powerGenerationMaintenance => {
		setDataPowerGenerationMaintenance(powerGenerationMaintenance);
		setModalDelete(true);
	};

	// Update Data
	const handleClickEdit = powerGenerationMaintenance => {
		setDataPowerGenerationMaintenance(powerGenerationMaintenance);
		handleToggleModal();
	};

	const columns = useMemo(
		() => [
			{
				header: 'Acciones', enableSorting: false, enableColumnFilter: false, enableGlobalFilter: false, enableColumnActions: false,
				accessorKey: 'powerGenerationMaintenance',
				size: 'auto',
				Cell: cellProps => {
					const powerGenerationMaintenanceData = cellProps.row.original;
					const { code } = powerGenerationMaintenanceData.maintenance_status;
					const isHidden = code === 'RZD' || code === 'CND';

					return (
						<React.Fragment>
							<div className='d-flex'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										<li className={`list-inline-item ${isHidden ? 'disabled' : ''}`}>
											<Link
												to='#'
												className={`remove-item-btn ${isHidden ? 'text-muted' : ''}`}
												title='Cancelar'
												onClick={(e) => {
													if (isHidden) {
														e.preventDefault(); // Evita navegación
														return;
													}
													handleClickDelete(powerGenerationMaintenanceData);
												}}
												aria-disabled={isHidden}
											>
												<i className='ri-indeterminate-circle-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
										<li className={`list-inline-item ${isHidden ? 'disabled' : ''}`}>
											<Link
												to='#'
												title='Ejecutar'
												onClick={(e) => {
													if (isHidden) {
														e.preventDefault(); // Evita navegación
														return;
													}
													setIsEdit(true);
													handleClickEdit(powerGenerationMaintenanceData);
												}}
												className={isHidden ? 'text-muted' : ''}
												aria-disabled={isHidden}
											>
												<i className='ri-play-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},
			{
				header: 'Estado',
				accessorKey: 'maintenance_status.name',
				dataType: 'multi-select',
				size: 'auto',
				Cell: ({ row }) => {
					const { code, name } = row?.original?.maintenance_status;
					let badgeClass = '';

					// Define el color del badge según el valor del código
					switch (code) {
						case 'RZD':
							badgeClass = 'bg-success-subtle text-success';
							break;
						case 'NRD':
							badgeClass = 'bg-danger-subtle text-danger';
							break;
						case 'PND':
							badgeClass = 'bg-warning-subtle text-warning';
							break;
						case 'CND':
							badgeClass = 'bg-dark-subtle text-dark';
							break;
					}
					return (
						<span className={`badge ${badgeClass}`}>
							{name.toString()}
						</span>
					);
				},
			},
			{
				header: 'Tipo',
				accessorKey: 'maintenance_type.name',
				size: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Tipo de Establecimiento',
				accessorKey: 'establishment_type.name',
				size: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Establecimiento',
				accessorKey: 'establishment.name',
				size: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Artefacto Naval',
				accessorKey: 'feeding_barges.name',
				size: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Responsable',
				accessorKey: 'responsible',
				size: 'auto',
				dataType: 'multi-select',
				accessorFn: row => {
					const responsible = row?.person?.name ?? row?.company?.name;
					return responsible ? responsible : 'Sin responsable';
				},
				Cell: ({ row }) => {
					const responsible = row?.original?.person?.name ??  row?.original?.company?.name
					return (
						<span>
							{responsible ? responsible : 'Sin responsable'}
						</span>
					);
				}
			},
			{
				header: 'Marca',
				accessorKey: 'power_generation_source.brand',
				size: 'auto',
				dataType: 'multi-select',
			},
			{
				header: 'Modelo',
				accessorKey: 'power_generation_source.model',
				size: 'auto',
				visible: false,
			},
			{
				header: 'Número de Serie',
				accessorKey: 'power_generation_source.serial_number',
				size: 'auto',
			},
			{
				header: 'Horómetro Actual',
				accessorKey: 'power_generation_source.hour_meter',
				dataType: 'separator',
				size: 'auto',
			},
			{
				header: 'Última Actualización',
				accessorKey: 'power_generation_source.date_hour_meter',
				dataType: 'date',
				size: 'auto',
			},

			{
				header: 'Horómetro Solicitud',
				accessorKey: 'hour_meter',
				dataType: 'separator',
				size: 'auto',
			},
			{
				header: 'Horómetro Mantención',
				accessorKey: 'maintenance_hour_meter',
				dataType: 'separator',
				size: 'auto',
			},
			{
				header: 'Fecha Visita Inicio',
				accessorKey: 'date_ini',
				dataType: 'date',
				size: 'auto',
			},
			{
				header: 'Fecha Visita Término',
				accessorKey: 'date_end',
				dataType: 'date',
				size: 'auto',
			},

			{ header: 'Observación', accessorKey: 'maintenance_observation' },
		],
		[handleClickEdit]
	);

	return (
		<React.Fragment>
			<Row>
				<Col lg={12}>
					<Card>
						{/* <CardHeader className='border-0 pb-0'>
							<div className='d-flex align-items-end justify-content-end'>
								<div className='flex-shrink-0'>
									<div className='d-flex flex-wrap gap-2'>
										<button
											className='btn btn-label btn-primary'
											onClick={() => {
												setIsEdit(false);
												setDataPowerGenerationMaintenance({});
												handleToggleModal();
											}}
										>
											<i className={`${Icons.plus} label-icon`}></i>{' '}
											Registrar Horómetro
										</button>
									</div>
								</div>
							</div>
						</CardHeader> */}
						<CardBody>
							<ODataGrid
								columns={columns}
								data={powerGenerationMaintenanceList || []}
								nameFile={'actividades_de_mantención'}
							/>
						</CardBody>
					</Card>
				</Col>
			</Row>

			<AddEditPowerGenerationMaintenanceModal
				dataPowerGenerationMaintenance={dataPowerGenerationMaintenance}
				isEdit={isEdit}
				isOpen={modal}
				handleToggleModal={handleToggleModal}
				activeTab={activeTab}
				toggleTab={toggleTab}
			/>
			<DeletePowerGenerationMaintenanceModal
				dataPowerGenerationMaintenance={dataPowerGenerationMaintenance}
				isOpen={modalDelete}
				handlerToggleModal={handleToggleModalDelete}
			/>
		</React.Fragment>
	);
};

export default PowerGenerationMaintenanceList;
